import axios from 'axios'
import {getSessionId} from './sessionManager'
import {getModuleAndPage} from './routeMapper'

const PRODUCT = 'proship-admin-web'
const BASE_URL = process.env.REACT_APP_ENDPOINT
const HEARTBEAT_URL = `${BASE_URL}external/ct/controltower/v1/wms/usage-analytics/heartbeat`
const EXPORT_URL = `${BASE_URL}external/ct/controltower/v1/wms/usage-analytics/events/export`
const ERROR_URL = `${BASE_URL}external/ct/controltower/v1/wms/usage-analytics/events/error`

// Private axios instance — separate from the global one so it never triggers
// the PrivateRoute.js response interceptor (token refresh / logout logic).
const trackingAxios = axios.create({baseURL: BASE_URL, timeout: 5000})

// Read token directly from localStorage — same key the app uses
trackingAxios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('userToken')
    if (token) config.headers.Authorization = `Bearer ${token}`
  } catch {}
  return config
})

function getPageMeta(pathname) {
  const {module: moduleName, page: pageName} = getModuleAndPage(pathname || window.location.pathname)
  return {moduleName, pageName}
}

// ── Navigation (POST /heartbeat with type NAVIGATION) ─────────────────────────
export function trackNav(pathname, activeSeconds) {
  try {
    const {moduleName, pageName} = getPageMeta(pathname)
    trackingAxios
      .post(HEARTBEAT_URL, {
        sessionId: getSessionId(),
        currentRoute: pathname,
        activeSeconds: activeSeconds || 0,
        product: PRODUCT,
        moduleName,
        pageName,
        type: 'NAVIGATION',
        event: 'NAVIGATION',
      })
      .catch(() => {})
  } catch {}
}

// ── Export (POST /events/export) ──────────────────────────────────────────────
export function trackExport(exportType, sourcePage, recordCount) {
  try {
    const payload = {exportType, sourcePage}
    if (recordCount !== undefined) payload.recordCount = recordCount
    trackingAxios.post(EXPORT_URL, payload).catch(() => {})
  } catch {}
}

// ── Error (POST /events/error) ────────────────────────────────────────────────
export function trackError(err) {
  try {
    trackingAxios
      .post(ERROR_URL, {
        sessionId: getSessionId(),
        route: window.location.pathname,
        errorCode: 0,
        errorMessage: err?.message || 'Network error',
        apiEndpoint: err?.config?.url ?? 'unknown',
      })
      .catch(() => {})
  } catch {}
}

// ── Heartbeat ─────────────────────────────────────────────────────────────────

const HEARTBEAT_INTERVAL_MS = 3 * 60 * 1000 // 3 minutes
let intervalId = null
let lastHeartbeatTime = Date.now()

function sendHeartbeat(isSessionEnd = false) {
  try {
    const token = localStorage.getItem('userToken')
    if (!token) return

    const now = Date.now()
    const activeSeconds = Math.round((now - lastHeartbeatTime) / 1000)
    lastHeartbeatTime = now

    const {moduleName, pageName} = getPageMeta(window.location.pathname)

    const payload = {
      sessionId: getSessionId(),
      activeSeconds,
      currentRoute: window.location.pathname,
      sessionEnd: isSessionEnd,
      type: 'HEARTBEAT',
      event: 'PAGE_VIEW',
      product: PRODUCT,
      moduleName,
      pageName,
    }

    if (isSessionEnd && navigator.sendBeacon) {
      // sendBeacon guarantees delivery even when the tab is closing
      const blob = new Blob([JSON.stringify(payload)], {type: 'application/json'})
      navigator.sendBeacon(HEARTBEAT_URL, blob)
    } else {
      trackingAxios.post(HEARTBEAT_URL, payload).catch(() => {})
    }
  } catch {}
}

function handleVisibilityChange() {
  if (document.hidden) {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
  } else {
    if (!intervalId) {
      sendHeartbeat()
      intervalId = setInterval(() => sendHeartbeat(), HEARTBEAT_INTERVAL_MS)
    }
  }
}

export function startHeartbeat() {
  if (intervalId) return // already running
  sendHeartbeat()
  intervalId = setInterval(() => sendHeartbeat(), HEARTBEAT_INTERVAL_MS)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('beforeunload', () => sendHeartbeat(true))
}

export function stopHeartbeat() {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange)
}
